import React from "react"
import { cn } from "@kit/design/utils"
import { useNavigationContext } from "@kit/design/navigation"
import { VisuallyHidden } from "@kit/design/visually-hidden"
import { useHasUnseenSponsorshipOffers } from "hooks/use-has-unseen-sponsorship-offers"
import { useTrackOnce } from "hooks/use-track-once"

/**
 * Dot marking unseen sponsorship offers in the vertical nav. Rendered in two
 * places so the cue follows the accordion:
 *
 * - On the Sponsorships item's `trailing` (default) — visible only while the
 *   Monetization group is open, since the item lives in the collapsible panel.
 * - On the Monetization group's `trailing` with `hideWhenGroupExpanded` — the
 *   group trigger carries Collapsible's `group/collapsible-trigger` +
 *   `aria-expanded`, so the dot is hidden while open and shows only when the
 *   group is collapsed.
 *
 * Safe to always pass as `trailing`: it renders nothing without an unseen
 * offer. The dot is the only cue for new offers, so it carries an sr-only
 * label rather than being purely decorative.
 */
export function SponsorshipNavDot({ hideWhenGroupExpanded = false }) {
  const hasUnseenOffers = useHasUnseenSponsorshipOffers()

  if (!hasUnseenOffers) return null

  return (
    <span
      className={cn(
        "flex items-center",
        hideWhenGroupExpanded &&
          "group-aria-expanded/collapsible-trigger:hidden"
      )}
    >
      <span aria-hidden="true" className="size-2 rounded-full bg-blue-500" />
      <VisuallyHidden>New sponsorship offers</VisuallyHidden>
    </span>
  )
}

/**
 * Fires the `Element Viewed` impression for the nav dot, so we can measure
 * whether the dot nudges offer acceptance.
 *
 * Deliberately lives outside `SponsorshipNavDot`: that component mounts
 * *twice* per nav (group chevron + Sponsorships item, both always mounted —
 * the group copy is hidden by CSS and the item copy sits in a
 * `hiddenUntilFound` panel), so tracking there would double-count. Same
 * reason it isn't in `useHasUnseenSponsorshipOffers` — both instances call it.
 *
 * `collapsed === false` is an exact proxy for "a dot is on screen", not an
 * approximation: `hideWhenGroupExpanded` keeps exactly one copy visible while
 * the sidebar is expanded, and `Navigation.Group`'s collapsed branch drops
 * `trailing` entirely, so neither copy renders. `useTrackOnce`'s ref guard
 * keeps it to one event even though `collapsed` flips on every Cmd+\ toggle.
 *
 * Must be rendered inside a `Navigation` to reach `useNavigationContext`.
 */
export function SponsorshipNavDotImpression() {
  const hasUnseenOffers = useHasUnseenSponsorshipOffers()
  const { collapsed, mobile } = useNavigationContext()

  useTrackOnce(
    "Element",
    "Viewed",
    {
      element_type: "indicator",
      name: "sponsorship_nav_dot",
      location: "vertical_nav",
      version: 1,
      is_mobile: mobile
    },
    { ready: hasUnseenOffers && !collapsed }
  )

  return null
}
